'use client';

import { ChevronRight, ExternalLink, MapPin, Video } from 'lucide-react';
import { ResponsiveSheet, SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import type { Congregation } from './LocationsFinder';

const DARK = '#1c2b39';

interface LiveSheetProps {
  open: boolean;
  onClose: () => void;
  cong: Congregation;
  svgUrl: string | null;
  address: string;
  watchUrl: string;
  pageUrl: string;
}

/**
 * What a tap on a live campus asks: watch the stream, or open the campus
 * page. A bottom sheet on phones, a centred modal on desktop, portalled to
 * the widget's body-level overlay host so it clears the site header.
 */
export function LiveSheet({
  open,
  onClose,
  cong,
  svgUrl,
  address,
  watchUrl,
  pageUrl,
}: LiveSheetProps) {
  const card =
    'flex w-full items-center gap-4 rounded-2xl border border-gray-200 bg-white p-4 text-left no-underline shadow-sm transition-all hover:scale-[1.01] hover:shadow-md active:scale-[0.99]';

  return (
    <ResponsiveSheet
      open={open}
      onClose={onClose}
      maxWidth="max-w-md"
      noPanelPadding
      panelClassName="bg-white"
      hideCloseButton
      header={
        <div
          className="relative overflow-hidden px-6 pt-5 pb-6"
          style={{ background: `linear-gradient(135deg, ${DARK} 0%, #243848 50%, #2d4a5c 100%)` }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              {svgUrl && (
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white">
                  <img src={svgUrl} alt="" className="h-8 w-8 object-contain" />
                </div>
              )}
              <div className="min-w-0">
                <div className="flex items-center gap-2.5">
                  <h2 className="font-display truncate text-[28px] leading-none tracking-wide text-white">
                    {cong.display_name}
                  </h2>
                  <span className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-red-600 px-2.5 py-1 text-[11px] font-bold tracking-wider text-white uppercase">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                    </span>
                    Live
                  </span>
                </div>
                <div className="mt-1.5 flex items-center gap-1.5 text-white/60">
                  <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                  <p className="truncate text-xs">{address}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    >
      <SheetPage name="main">
        <div className="px-6 py-5">
          <p className="text-sm text-gray-600">
            <span className="font-semibold text-[#1c2b39]">
              {cong.display_name} is streaming right now.
            </span>{' '}
            Join the service online, or keep going to the campus page.
          </p>
          <div className="mt-4 space-y-3">
            <a href={watchUrl} target="_blank" rel="noopener noreferrer" className={card}>
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-50">
                <Video className="h-6 w-6 text-red-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-gray-800">Watch the stream</p>
              </div>
              <ExternalLink className="h-5 w-5 flex-shrink-0 text-gray-300" />
            </a>
            <a href={pageUrl} className={card}>
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-[#299c8f]/10">
                <MapPin className="h-6 w-6 text-[#299c8f]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-gray-800">Campus page</p>
              </div>
              <ChevronRight className="h-5 w-5 flex-shrink-0 text-gray-300" />
            </a>
          </div>
        </div>
      </SheetPage>
    </ResponsiveSheet>
  );
}
